import React from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { addPhysicianOptions, getPhysicianOptions, OrderDeliveryMethodOptions, editPhysicianOptions } from '@generated';
import Yup from '~/util/yup';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { AddressFormField } from '~/shared/forms/controls/fields/AddressFormField';
import { StethoscopeIcon } from 'lucide-react';
import { LibraryForm } from '../shared/LibraryForm';
import { useNavigate, useParams } from 'react-router-dom';
import { PhoneNumberFormField } from '~/shared/forms/controls/fields/text-fields/PhoneNumberFormField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import type { FormProps } from '~/shared/forms/Form';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { getSearchQueryKey } from '~/util/http/useSearch';

const validationSchema = Yup.object().shape({
	firstName: Yup.string().label('First Name').nullable().required(),
	lastName: Yup.string().label('Last Name').nullable().required(),
	suffix: Yup.string().label('Suffix').nullable(),
	practiceName: Yup.string().label('Practice Name').nullable().required(),
	npiNumber: Yup.string().label('NPI Number').nullable().required().npi(),
	address: Yup.object().label('Address').address().required(),
	phoneNumber: Yup.string().label('Phone Number').nullable().required().phoneNumber(),
	faxNumber: Yup.string().label('Fax Number').nullable().phoneNumber(),
	emailAddress: Yup.string().label('Email Address').nullable().email(),
	notes: Yup.string().label('Notes').nullable(),
	orderDeliveryMethodId: Yup.number().label('Order Delivery Method').nullable(),
	faxDeliveryFrequencyUnitOfTimeId: Yup.string().label('Auto Fax Frequency').nullable(),
	faxDeliveryFrequencyFrequency: Yup.number()
		.label('Every')
		.when('faxDeliveryFrequencyUnitOfTimeId', {
			is: (val?: string) => !!val,
			then: schema => schema.required('Fax Frequency Every # is required').min(1),
			otherwise: schema => schema.nullable()
		}),
	resendOrderFrequencyDays: Yup.number().label('Resend Order Frequency (Days)').nullable().min(1)
});

export function EditPhysicianForm() {
	const { id } = useParams<'id'>();
	const options = getPhysicianOptions(id!);
	const { data: physician } = useSuspenseQuery(options);

	return (
		<PhysicianForm
			initialValues={{ ...physician, physicianId: id }}
			saveMutation={{
				...editPhysicianOptions,
				invalidateQueryKeys: [options.queryKey]
			}}
			title={`Edit Physician: ${physician.fullName}`}
			description={`Make changes to the physician ${physician.fullName}`}
		/>
	);
}

export function AddPhysicianForm() {
	const navigate = useNavigate();
	return (
		<PhysicianForm
			initialValues={{
				firstName: null,
				lastName: null,
				suffix: null,
				address: null,
				phoneNumber: null,
				faxNumber: null,
				emailAddress: null,
				physicianHours: null,
				notes: null,
				npiNumber: null,
				practiceName: null,
				orderDeliveryMethodId: null,
				faxDeliveryFrequencyUnitOfTimeId: null,
				faxDeliveryFrequencyFrequency: null
			}}
			saveMutation={{
				...addPhysicianOptions,
				invalidateQueryKeys: [getSearchQueryKey('physician')]
			}}
			onSuccess={() => navigate('/settings/libraries/physicians')}
			title="Add Physician"
			description="Add a physician to the system"
		/>
	);
}

function PhysicianForm(props: Pick<FormProps, 'initialValues' | 'saveMutation' | 'onSuccess'> & { title: string; description: string }) {
	const navigate = useNavigate();
	return (
		<LibraryForm
			{...props}
			TitleIcon={StethoscopeIcon}
			validationSchema={validationSchema}
			errorAction={{
				errorActionText: 'Return to Physician Library',
				errorAction: () => navigate('/settings/libraries/pharmacies')
			}}
		>
			<div className="grid grid-cols-2 gap-6">
				<TextFormField name="firstName" />
				<TextFormField name="lastName" />
				<TextFormField name="suffix" />
				<TextFormField name="practiceName" />
				<TextFormField name="npiNumber" />
				<PhoneNumberFormField name="phoneNumber" />
				<PhoneNumberFormField name="faxNumber" />

				<TextFormField name="emailAddress" className="col-span-2" />
				<SelectFormField name="orderDeliveryMethodId" options={OrderDeliveryMethodOptions} />
				<TextAreaFormField name="notes" className="col-span-2" />
				<div className="col-span-2">
					<AddressFormField name="address" />
				</div>
			</div>
		</LibraryForm>
	);
}
